import * as React from "react";
import { createPortal } from "react-dom";
import { cva, type VariantProps } from "class-variance-authority";
import { Calendar as CalendarIcon, X } from "lucide-react";
import Calendar from "react-calendar";
// @ts-ignore - CSS import doesn't have type definitions
import "react-calendar/dist/Calendar.css";
import { cn } from "../../lib/utils";
import { FormHeader } from "../FormHeader";
import { FormFooter } from "../FormFooter";

// Type for react-calendar's Value
// ValuePiece = Date | null
// Value = ValuePiece | Range<ValuePiece> = Date | null | [Date | null, Date | null]
type CalendarValue = Date | null | [Date | null, Date | null];

const datePickerVariants = cva(
  "relative flex items-center gap-2 border rounded-large transition-all font-functional font-size-100 leading-100",
  {
    variants: {
      size: {
        small: "h-[28px] px-3 text-xs gap-2",
        medium: "h-[36px] px-4 text-sm gap-2",
        large: "h-[44px] px-5 text-base gap-3",
      },
      validationState: {
        none: `
        border-action-outline-neutral-faded 
        hover:border-action-outline-primary-hover 
        focus-within:border-action-outline-primary-hover 
        focus-within:ring-2 
        ring-action-outline-primary-faded-hover`,
        positive: `
          border-action-outline-positive-default 
          focus-within:border-action-outline-positive-hover
          focus-within:ring-2
          ring-action-outline-positive-faded-hover`,
        negative: `border-action-outline-negative-default 
          focus-within:border-action-outline-negative-hover
          focus-within:ring-2 
          ring-action-outline-negative-faded-hover`,
      },
      isDisabled: {
        true: `
        border-[var(--border-width-thinner)]
        hover:border-action-outline-neutral-disabled
        border-action-outline-neutral-disabled
        bg-surface-fill-neutral-intense cursor-not-allowed opacity-60`,
        false: "bg-surface-fill-neutral-intense",
      },
    },
    defaultVariants: {
      size: "medium",
      validationState: "none",
      isDisabled: false,
    },
  }
);

export interface DatePickerProps
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    "onChange" | "defaultValue"
  > {
  /**
   * Selected date (controlled)
   */
  value?: Date | string | null;
  /**
   * Default selected date (uncontrolled)
   */
  defaultValue?: Date | string | null;
  /**
   * Callback when date changes
   */
  onChange?: (date: Date | null) => void;
  /**
   * Placeholder text when no date is selected
   */
  placeholder?: string;
  /**
   * Label for the date picker
   */
  label?: string;
  /**
   * Helper text below the date picker
   */
  helperText?: string;
  /**
   * Error text (overrides helperText when present)
   */
  errorText?: string;
  /**
   * Success text (overrides helperText when present)
   */
  successText?: string;
  /**
   * Validation state for the date picker
   */
  validationState?: "none" | "positive" | "negative";
  /**
   * Whether the date picker is disabled
   */
  isDisabled?: boolean;
  /**
   * Whether the date picker is required
   */
  isRequired?: boolean;
  /**
   * Whether the date picker is optional
   */
  isOptional?: boolean;
  /**
   * Size of the date picker
   */
  size?: "small" | "medium" | "large";
  /**
   * Show clear button when value is selected
   */
  showClearButton?: boolean;
  /**
   * Callback when clear button is clicked
   */
  onClear?: () => void;
  /**
   * Custom class for the container
   */
  containerClassName?: string;
  /**
   * Custom class for the label
   */
  labelClassName?: string;
  /**
   * Custom class for the trigger button
   */
  triggerClassName?: string;
  /**
   * Custom class for the calendar popup
   */
  calendarClassName?: string;
  /**
   * Minimum selectable date
   */
  minDate?: Date | string;
  /**
   * Maximum selectable date
   */
  maxDate?: Date | string;
  /**
   * Date format function
   */
  formatDate?: (date: Date) => string;
  /**
   * Date format pattern string (e.g., "YYYY-MM-DD", "MM/DD/YYYY")
   * If provided, this will be used instead of formatDate
   */
  format?: string;
  /**
   * Info heading for tooltip
   */
  infoHeading?: string;
  /**
   * Info description for tooltip
   */
  infoDescription?: string;
  /**
   * Custom Link component to render on the right side
   */
  LinkComponent?: React.ReactNode;
  /**
   * Link text
   */
  linkText?: string;
  /**
   * Link href
   */
  linkHref?: string;
  /**
   * Link click handler
   */
  onLinkClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

// Helper functions
const parseDate = (date: Date | string | null | undefined): Date | null => {
  if (!date) return null;
  if (date instanceof Date) return date;
  if (typeof date === "string") {
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const formatDateDefault = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Helper function to format date based on format string
const formatDateByPattern = (date: Date, format: string): string => {
  const day = date.getDate();
  const month = date.getMonth() + 1; // getMonth() returns 0-11
  const year = date.getFullYear();

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthNamesFull = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const dayNamesFull = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  // Pad numbers with leading zeros
  const pad = (n: number, length: number = 2): string => {
    const str = n.toString();
    if (str.length >= length) return str;
    return "0".repeat(length - str.length) + str;
  };

  let formatted = format;

  // Replace format patterns
  formatted = formatted.replace(/YYYY/g, year.toString());
  formatted = formatted.replace(/YY/g, year.toString().slice(-2));
  formatted = formatted.replace(/MMMM/g, monthNamesFull[month - 1]);
  formatted = formatted.replace(/MMM/g, monthNames[month - 1]);
  formatted = formatted.replace(/MM/g, pad(month));
  formatted = formatted.replace(/M/g, month.toString());
  formatted = formatted.replace(/DDDD/g, dayNamesFull[date.getDay()]);
  formatted = formatted.replace(/DDD/g, dayNames[date.getDay()]);
  formatted = formatted.replace(/DD/g, pad(day));
  formatted = formatted.replace(/D/g, day.toString());

  return formatted;
};

export const DatePicker = React.forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      className,
      value: controlledValue,
      defaultValue,
      onChange,
      placeholder = "Select a date",
      label,
      helperText,
      errorText,
      successText,
      validationState = "none",
      isDisabled = false,
      isRequired = false,
      isOptional = false,
      size = "medium",
      showClearButton = true,
      onClear,
      containerClassName,
      labelClassName,
      triggerClassName,
      calendarClassName,
      minDate,
      maxDate,
      formatDate = formatDateDefault,
      format,
      infoHeading,
      infoDescription,
      LinkComponent,
      linkText,
      linkHref,
      onLinkClick,
      ...props
    },
    ref
  ) => {
    const [uncontrolledValue, setUncontrolledValue] =
      React.useState<Date | null>(parseDate(defaultValue));
    const [isOpen, setIsOpen] = React.useState(false);
    const datePickerRef = React.useRef<HTMLDivElement>(null);
    const calendarRef = React.useRef<HTMLDivElement>(null);
    const [dropdownPlacement, setDropdownPlacement] = React.useState<
      "top" | "bottom"
    >("bottom");
    const [isInsideModal, setIsInsideModal] = React.useState(false);
    const [position, setPosition] = React.useState({
      top: 0,
      left: 0,
      width: 0,
      bottom: 0,
    });
    const [calendarHeight, setCalendarHeight] = React.useState(300); // Default height estimate

    const value =
      controlledValue !== undefined
        ? parseDate(controlledValue)
        : uncontrolledValue;

    const hasValue = value !== null;

    // Create a formatter function that uses format prop if provided, otherwise formatDate
    const formatDateValue = React.useCallback(
      (date: Date): string => {
        if (format) {
          return formatDateByPattern(date, format);
        }
        return formatDate(date);
      },
      [format, formatDate]
    );

    // Determine which helper text to show
    const displayHelperText = errorText || successText || helperText;
    const currentValidationState = errorText
      ? "negative"
      : successText
      ? "positive"
      : validationState;

    const handleOpenChange = (newOpen: boolean) => {
      if (!isDisabled) {
        setIsOpen(newOpen);
      }
    };

    const toggleOpen = () => {
      handleOpenChange(!isOpen);
    };

    const handleCalendarChange = (
      value: CalendarValue,
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      // react-calendar can return Date, Date[], or null
      // We only support single date selection, so we take the first date if it's an array
      if (!value) {
        if (controlledValue === undefined) {
          setUncontrolledValue(null);
        }
        onChange?.(null);
        setIsOpen(false);
        return;
      }

      const selectedDate = Array.isArray(value) ? value[0] : value;

      // Ensure we have a valid Date object
      if (selectedDate instanceof Date) {
        if (controlledValue === undefined) {
          setUncontrolledValue(selectedDate);
        }
        onChange?.(selectedDate);
        setIsOpen(false);
      }
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onClear) {
        onClear();
      } else {
        if (controlledValue === undefined) {
          setUncontrolledValue(null);
        }
        onChange?.(null);
      }
    };

    const updateDropdownPlacement = React.useCallback(() => {
      if (typeof window === "undefined") return;
      const trigger = datePickerRef.current;
      if (!trigger) return;

      const triggerRect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;

      const calendarHeight = calendarRef.current
        ? calendarRef.current.offsetHeight
        : 0;

      if (calendarHeight === 0) {
        setDropdownPlacement(spaceBelow >= spaceAbove ? "bottom" : "top");
        return;
      }

      if (spaceBelow >= calendarHeight || spaceBelow >= spaceAbove) {
        setDropdownPlacement("bottom");
      } else {
        setDropdownPlacement("top");
      }
    }, []);

    // Check if date picker is inside a modal
    React.useEffect(() => {
      if (isOpen && datePickerRef.current) {
        let element: HTMLElement | null = datePickerRef.current;
        let foundModal = false;
        while (element && !foundModal) {
          const styles = window.getComputedStyle(element);
          const zIndex = parseInt(styles.zIndex, 10);
          // Check if element has modal z-index (10000) or is a modal container
          if (zIndex === 10000 || element.getAttribute("role") === "dialog") {
            foundModal = true;
            setIsInsideModal(true);
            break;
          }
          element = element.parentElement;
        }
        if (!foundModal) {
          setIsInsideModal(false);
        }
      }
    }, [isOpen]);

    // Update position when calendar opens or window resizes
    React.useEffect(() => {
      if (isOpen && datePickerRef.current) {
        const updatePosition = () => {
          const rect = datePickerRef.current?.getBoundingClientRect();
          if (rect) {
            setPosition({
              top: rect.top,
              left: rect.left,
              width: rect.width,
              bottom: rect.bottom,
            });
            // Update dropdown placement based on available space
            updateDropdownPlacement();
          }
        };

        updatePosition();
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);

        return () => {
          window.removeEventListener("resize", updatePosition);
          window.removeEventListener("scroll", updatePosition, true);
        };
      }
    }, [isOpen, updateDropdownPlacement]);

    React.useEffect(() => {
      if (!isOpen) return;
      if (typeof window === "undefined") return;

      // Use requestAnimationFrame to ensure calendar is rendered before calculating placement
      let rafId = requestAnimationFrame(() => {
        updateDropdownPlacement();
      });

      const handleUpdate = () => {
        updateDropdownPlacement();
        // Also update position when scrolling/resizing
        if (datePickerRef.current) {
          const rect = datePickerRef.current.getBoundingClientRect();
          setPosition({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            bottom: rect.bottom,
          });
        }
      };

      window.addEventListener("resize", handleUpdate);
      window.addEventListener("scroll", handleUpdate, true);

      return () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener("resize", handleUpdate);
        window.removeEventListener("scroll", handleUpdate, true);
      };
    }, [isOpen, updateDropdownPlacement]);

    React.useEffect(() => {
      if (isOpen) {
        // Delay to ensure calendar is rendered
        const timer = setTimeout(() => {
          updateDropdownPlacement();
        }, 0);
        return () => clearTimeout(timer);
      }
    }, [isOpen, updateDropdownPlacement]);

    // Measure calendar height and adjust position after render
    React.useLayoutEffect(() => {
      if (isOpen && calendarRef.current && datePickerRef.current) {
        const measuredHeight = calendarRef.current.offsetHeight;
        setCalendarHeight(measuredHeight);

        const rect = datePickerRef.current.getBoundingClientRect();

        // Recalculate placement if needed based on actual calendar height
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        if (measuredHeight > spaceBelow && spaceAbove > spaceBelow) {
          setDropdownPlacement("top");
        } else {
          setDropdownPlacement("bottom");
        }

        // Update position
        setPosition({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          bottom: rect.bottom,
        });
      }
    }, [isOpen]);

    // Close calendar when clicking outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          datePickerRef.current &&
          !datePickerRef.current.contains(event.target as Node) &&
          calendarRef.current &&
          !calendarRef.current.contains(event.target as Node)
        ) {
          handleOpenChange(false);
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [isOpen]);

    // Close on escape key
    React.useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          handleOpenChange(false);
        }
      };

      if (isOpen) {
        document.addEventListener("keydown", handleEscape);
        return () => {
          document.removeEventListener("keydown", handleEscape);
        };
      }
    }, [isOpen]);

    const minDateParsed = parseDate(minDate);
    const maxDateParsed = parseDate(maxDate);

    const sizeConfig = {
      small: {
        gap: "gap-2",
      },
      medium: {
        gap: "gap-2",
      },
      large: {
        gap: "gap-3",
      },
    };

    return (
      <div
        className={cn(
          "w-full flex flex-col",
          sizeConfig[size].gap,
          containerClassName
        )}
      >
        {label && (
          <FormHeader
            label={label}
            size={size}
            isRequired={isRequired}
            isOptional={isOptional}
            infoHeading={infoHeading}
            infoDescription={infoDescription}
            LinkComponent={LinkComponent}
            linkText={linkText}
            linkHref={linkHref}
            onLinkClick={onLinkClick}
            htmlFor={props.id}
            className="mb-2"
            labelClassName={labelClassName}
          />
        )}

        <div
          ref={datePickerRef}
          className={cn(
            datePickerVariants({
              size,
              validationState: currentValidationState,
              isDisabled,
            }),
            "relative w-full cursor-pointer",
            className
          )}
          onClick={!isDisabled ? toggleOpen : undefined}
          role="button"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-disabled={isDisabled}
          {...props}
        >
          <CalendarIcon
            className={cn(
              "shrink-0 w-4 h-4",
              isDisabled
                ? "text-surface-ink-neutral-disabled"
                : currentValidationState === "positive"
                ? "text-feedback-ink-positive-intense"
                : currentValidationState === "negative"
                ? "text-feedback-ink-negative-subtle"
                : "text-surface-ink-neutral-muted"
            )}
          />

          <span
            className={cn(
              "flex-1 text-left truncate",
              !hasValue && "text-surface-ink-neutral-muted",
              isDisabled && "text-surface-ink-neutral-disabled"
            )}
          >
            {hasValue && value ? formatDateValue(value) : placeholder}
          </span>

          {showClearButton && hasValue && !isDisabled && (
            <button
              type="button"
              onClick={handleClear}
              className="shrink-0 flex items-center justify-center text-surface-ink-neutral-muted hover:text-surface-ink-neutral-normal transition-colors"
              tabIndex={-1}
              aria-label="Clear date"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <FormFooter
          helperText={displayHelperText}
          validationState={
            currentValidationState === "none"
              ? "default"
              : currentValidationState
          }
          size={size}
          isDisabled={isDisabled}
          className="mt-1"
        />

        {/* Calendar Popup Portal */}
        {typeof document !== "undefined" &&
          isOpen &&
          !isDisabled &&
          (() => {
            // Calculate calendar position using fixed positioning (viewport-relative)
            const gap = 4; // 4px gap between trigger and calendar

            const calendarTop =
              dropdownPlacement === "bottom"
                ? position.bottom + gap
                : position.top - calendarHeight - gap;

            const calendarPopup = (
              <div
                ref={calendarRef}
                style={{
                  position: "fixed",
                  top: `${calendarTop}px`,
                  left: `${position.left}px`,
                  zIndex: isInsideModal ? 10001 : 9999,
                }}
                className={cn(
                  "bg-surface-fill-neutral-intense rounded-large shadow-lg p-4 w-fit",
                  calendarClassName
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="react-calendar-wrapper w-fit">
                  <Calendar
                    onChange={handleCalendarChange}
                    value={value ?? null}
                    minDate={minDateParsed ?? undefined}
                    maxDate={maxDateParsed ?? undefined}
                    locale="en-US"
                    formatShortWeekday={(locale, date) => {
                      const weekdayNames = [
                        "Su",
                        "Mo",
                        "Tu",
                        "We",
                        "Th",
                        "Fr",
                        "Sa",
                      ];
                      return weekdayNames[date.getDay()];
                    }}
                  />
                </div>
              </div>
            );

            return createPortal(calendarPopup, document.body);
          })()}
      </div>
    );
  }
);

DatePicker.displayName = "DatePicker";

export { datePickerVariants };
